// The agents index (/agents, docs/09 §4) — List archetype in a workspace
// container (UI-LAW §4c): a command-palette/direct-URL destination, not a rail
// item. Lists `GET /v1/agents` (keyset), narrowed by the resolved working team
// when present; team names resolve from the ONE `/v1/teams` list read (never a
// per-row fan-out). `+ Agent` opens the same create form as the sidebar
// selector (AgentCreateForm) in a Popover; "Open in scope" sets the working
// scope and lands on Transcripts.
// Aggregate activity columns wait for a real /v1 aggregate read.

import { useMemo, useRef, useState } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { useNavigate } from "@tanstack/react-router";

import type { components } from "../api/schema";
import { apiClient } from "../lib/apiClient";
import { ApiRequestError } from "../lib/errors";
import { formatDateTime } from "../lib/format";
import { queryKeys } from "../lib/queryKeys";
import { shortResourceId } from "../lib/shellNavigation";
import { useListQuery } from "../lib/useListQuery";
import { useResolvedWorkingTeamId } from "../lib/workingScope";
import {
  AgentCreateForm,
  Button,
  EmptyState,
  Page,
  PageHeader,
  Popover,
  RouterLink,
  type TeamSelectorItem,
} from "../ui";
import { NameWithId } from "./shared/NameWithId";
import { PaginatedTableSection } from "./shared/PaginatedTableSection";

type AgentRow = components["schemas"]["AgentResponse"];

interface AgentColumnInput {
  onOpenInScope: (agent: AgentRow) => void;
  teamNamesById: ReadonlyMap<string, string>;
}

function agentColumns({
  onOpenInScope,
  teamNamesById,
}: AgentColumnInput): Array<ColumnDef<AgentRow>> {
  return [
    {
      header: "Name",
      id: "name",
      accessorFn: (agent) => agent.display_name,
      cell: ({ row }) => (
        <NameWithId
          id={row.original.id}
          idLabel="Agent ID"
          name={
            <RouterLink
              params={{ agentId: row.original.id }}
              search={{}}
              to="/agents/$agentId"
            >
              {row.original.display_name}
            </RouterLink>
          }
        />
      ),
    },
    {
      header: "Team",
      id: "team",
      accessorFn: (agent) =>
        teamNamesById.get(agent.team_id) ??
        `Team ${shortResourceId(agent.team_id)}`,
    },
    {
      header: "Created",
      accessorKey: "created_at",
      cell: ({ row }) => formatDateTime(row.original.created_at),
    },
    {
      header: "Actions",
      id: "actions",
      cell: ({ row }) => (
        <Button
          size="sm"
          type="button"
          variant="secondary"
          onClick={() => {
            onOpenInScope(row.original);
          }}
        >
          Open in scope
        </Button>
      ),
    },
  ];
}

export function AgentsIndex() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const workingTeamId = useResolvedWorkingTeamId();
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  // The index respects the resolved working team when present; with no team it
  // lists every agent the caller can see.
  const agents = useListQuery("/v1/agents", {
    params: { team_id: workingTeamId },
    queryKey: queryKeys.agents.list({
      surface: "agents-index",
      team_id: workingTeamId,
    }),
  });

  // ONE list read resolves team display names for the Team column and feeds
  // the create form's team select.
  const teams = useListQuery("/v1/teams", {
    queryKey: queryKeys.teams.list({ surface: "agents-index" }),
  });

  const createAgent = useMutation({
    mutationFn: async (input: { displayName: string; teamId: string }) => {
      const { data, error } = await apiClient.POST("/v1/agents", {
        body: { display_name: input.displayName, team_id: input.teamId },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    onSuccess: async (created) => {
      setIsCreateOpen(false);
      await queryClient.invalidateQueries({ queryKey: queryKeys.agents.all });
      // The same one-switch semantic as the selector: creating an agent
      // scopes to it, landing on its hub.
      void navigate({
        params: { agentId: created.id },
        search: {},
        to: "/agents/$agentId",
      });
    },
  });

  return (
    <AgentsIndexView
      createErrorMessage={
        createAgent.isError ? createAgent.error.message : undefined
      }
      hasNextPage={agents.hasNextPage}
      isCreateOpen={isCreateOpen}
      isCreatePending={createAgent.isPending}
      isFetchingNextPage={agents.isFetchingNextPage}
      isPending={agents.isPending}
      isTeamsError={teams.errorMessage !== undefined}
      isTeamsPending={teams.isPending}
      items={agents.items}
      listErrorMessage={agents.errorMessage}
      listStateKey={agents.listStateKey}
      teams={teams.items.map((team) => ({
        id: team.id,
        label: team.display_name,
      }))}
      workingTeamId={workingTeamId}
      onCreateAgent={(input) => {
        createAgent.mutate(input);
      }}
      onCreateOpenChange={(open) => {
        if (open) {
          createAgent.reset();
        }
        setIsCreateOpen(open);
      }}
      onLoadMore={agents.fetchNextPage}
      onOpenInScope={(agent) => {
        // The scope-jump affordance (docs/09 §4): set the canonical agent
        // scope and land on that scope's Transcripts.
        void navigate({
          search: { agent: agent.id },
          to: "/transcripts",
        });
      }}
    />
  );
}

export interface AgentsIndexViewProps {
  createErrorMessage?: string;
  hasNextPage: boolean;
  isCreateOpen: boolean;
  isCreatePending: boolean;
  isFetchingNextPage: boolean;
  isPending: boolean;
  isTeamsError: boolean;
  isTeamsPending: boolean;
  items: AgentRow[];
  listErrorMessage?: string;
  listStateKey: string;
  onCreateAgent: (input: { displayName: string; teamId: string }) => void;
  onCreateOpenChange: (open: boolean) => void;
  onLoadMore: () => void;
  onOpenInScope: (agent: AgentRow) => void;
  teams: readonly TeamSelectorItem[];
  workingTeamId?: string;
}

export function AgentsIndexView({
  createErrorMessage,
  hasNextPage,
  isCreateOpen,
  isCreatePending,
  isFetchingNextPage,
  isPending,
  isTeamsError,
  isTeamsPending,
  items,
  listErrorMessage,
  listStateKey,
  onCreateAgent,
  onCreateOpenChange,
  onLoadMore,
  onOpenInScope,
  teams,
  workingTeamId,
}: AgentsIndexViewProps) {
  const createAnchorRef = useRef<HTMLButtonElement>(null);
  const teamNamesById: ReadonlyMap<string, string> = useMemo(
    () => new Map(teams.map((team) => [team.id, team.label])),
    [teams],
  );
  const columns = useMemo(
    () => agentColumns({ onOpenInScope, teamNamesById }),
    [onOpenInScope, teamNamesById],
  );

  return (
    <Page variant="workspace">
      <PageHeader
        actions={
          <div className="marker-popover-anchor">
            <Button
              ref={createAnchorRef}
              aria-expanded={isCreateOpen}
              aria-haspopup="dialog"
              size="sm"
              type="button"
              onClick={() => {
                onCreateOpenChange(!isCreateOpen);
              }}
            >
              + Agent
            </Button>
            <Popover
              align="end"
              anchorRef={createAnchorRef}
              aria-label="Create agent"
              isOpen={isCreateOpen}
              onDismiss={() => {
                onCreateOpenChange(false);
              }}
            >
              <AgentCreateForm
                createError={createErrorMessage ?? null}
                defaultTeamId={workingTeamId}
                isCreating={isCreatePending}
                isTeamsError={isTeamsError}
                isTeamsPending={isTeamsPending}
                teams={teams}
                onCancel={() => {
                  onCreateOpenChange(false);
                }}
                onCreateAgent={onCreateAgent}
              />
            </Popover>
          </div>
        }
      />
      <PaginatedTableSection
        ariaLabel="Agents"
        columns={columns}
        emptyState={
          <EmptyState
            action={
              <Button
                type="button"
                onClick={() => {
                  onCreateOpenChange(true);
                }}
              >
                + Agent
              </Button>
            }
            title="No agents"
          />
        }
        enableSorting
        errorMessage={listErrorMessage}
        getRowId={(agent) => agent.id}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        isPending={isPending}
        items={items}
        listStateKey={listStateKey}
        skeletonLabel="Loading agents"
        skeletonRows={5}
        virtual={{ fill: true, interactiveRows: true }}
        onLoadMore={onLoadMore}
      />
    </Page>
  );
}
